/**
 * Zora homepage motion — scroll reveals, stagger, counters, subtle parallax.
 * Respects prefers-reduced-motion.
 */
(function () {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* ── Scroll reveal ── */
  function initReveal() {
    const els = document.querySelectorAll('.zm-reveal');
    if (!els.length) return;

    if (reduced) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -48px 0px' }
    );

    els.forEach((el) => io.observe(el));
  }

  /* ── Stagger children in grids ── */
  function initStagger() {
    const selectors = [
      '.zm-stats-grid',
      '.zm-mech-grid',
      '.zm-ba-benefits',
      '.zm-compare-table',
      '.zm-gallery-lifestyle',
      '.zm-feature-list',
    ];

    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((container) => {
        container.setAttribute('data-zm-stagger', '');

        const skipHead = container.classList.contains('zm-compare-table');
        const children = [...container.children].filter((child) => {
          if (skipHead && child.classList.contains('zm-compare-row--head')) return false;
          return true;
        });

        children.forEach((child, i) => {
          child.classList.add('zm-stagger-item');
          child.style.setProperty('--zm-stagger-i', i);
        });
      });
    });

    if (reduced) {
      document.querySelectorAll('[data-zm-stagger]').forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
    );

    document.querySelectorAll('[data-zm-stagger]').forEach((el) => io.observe(el));
  }

  /* ── Count-up stats & rating ── */
  function initCounters() {
    const counters = document.querySelectorAll('.zm-stat b, .zm-reviews-head .zm-num');
    if (!counters.length) return;

    if (reduced) return;

    function animate(el) {
      if (el.dataset.zmCounted) return;
      const raw = el.textContent.trim();
      const match = raw.match(/^(\d+(?:\.\d+)?)(.*)$/);
      if (!match) return;

      const end = parseFloat(match[1]);
      const suffix = match[2] || '';
      const isFloat = raw.includes('.');
      const duration = 1400;
      const start = performance.now();

      el.dataset.zmCounted = '1';

      function tick(now) {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        const val = eased * end;
        el.textContent = (isFloat ? val.toFixed(1) : Math.round(val)) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      }

      requestAnimationFrame(tick);
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animate(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => {
      if (/\d/.test(el.textContent)) io.observe(el);
    });
  }

  /* ── Subtle copy parallax in hero ── */
  function initParallax() {
    if (reduced) return;

    const copy = document.querySelector('.zm-hero__copy');
    if (!copy) return;

    let ticking = false;

    function update() {
      ticking = false;
      const y = Math.min(window.scrollY * 0.025, 32);
      copy.style.transform = `translate3d(0, ${y * 0.5}px, 0)`;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
  }

  /* ── Magnetic buttons (desktop) ── */
  function initMagneticButtons() {
    if (reduced || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document.querySelectorAll('.zm-btn').forEach((btn) => {
      btn.classList.add('zm-btn--magnetic');

      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) * 0.14;
        const y = (e.clientY - r.top - r.height / 2) * 0.18;
        btn.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  }

  /* ── Active nav link on scroll ── */
  function initNavSpy() {
    const links = [...document.querySelectorAll('.zm-nav-links a[href^="#"]')];
    if (!links.length) return;

    const sections = links
      .map((a) => {
        const id = a.getAttribute('href').slice(1);
        return document.getElementById(id);
      })
      .filter(Boolean);

    if (!sections.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.id;
          links.forEach((a) => {
            a.classList.toggle('is-active', a.getAttribute('href') === `#${id}`);
          });
        });
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: 0 }
    );

    sections.forEach((s) => io.observe(s));
  }

  /* ── Section in-view glow (CSS hook) ── */
  function initSectionInView() {
    if (reduced) return;

    document.querySelectorAll('.zm-section, .zm-hero, .zm-cta-section').forEach((section) => {
      const io = new IntersectionObserver(
        ([entry]) => {
          section.classList.toggle('is-inview', entry.isIntersecting);
        },
        { threshold: 0.15 }
      );
      io.observe(section);
    });
  }

  /* ── Tilt on review cards ── */
  function initCardTilt() {
    if (reduced || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document.querySelectorAll('.zm-review-card, .zm-mech-card, .zm-doctor-card').forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = `perspective(800px) rotateX(${py * -4}deg) rotateY(${px * 4}deg) translateY(-2px)`;
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = '';
      });
    });
  }

  /* ── Hero spin video: force muted autoplay ── */
  function initHeroVideo() {
    const video = document.querySelector('.zm-hero__video');
    if (!video) return;

    if (reduced) {
      video.pause();
      video.removeAttribute('autoplay');
      return;
    }

    const tryPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    tryPlay();
    video.addEventListener('loadeddata', tryPlay, { once: true });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tryPlay();
    });
  }

  onReady(() => {
    document.documentElement.classList.add('zm-motion-ready');
    initReveal();
    initStagger();
    initCounters();
    initParallax();
    initMagneticButtons();
    initNavSpy();
    initSectionInView();
    initCardTilt();
    initHeroVideo();
  });
})();
